/**
 * Security Dashboard — staff-only operational view.
 *
 * v77.99.116 scaffold. Three audiences (driven by the backend
 * response):
 *   - primary_admin / support_admin / support_viewer — full view,
 *     including recent admin actions and failed-login source IPs.
 *   - auditor — evidence mode: counts, control statuses, and policy
 *     metadata only. No actor or target identifiers shown.
 *
 * Currently a scaffold — surfaces the live dashboard payload and
 * leaves room for richer cards (alerts, certificate expiry charts,
 * dependency-health timelines) in later iterations.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, AlertTriangle, Activity, Users2, KeyRound, FileCheck, Server, RefreshCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

const fmt = (v) => (v == null ? "—" : typeof v === "number" ? v.toLocaleString() : v);

const SeverityPill = ({ s }) => {
  const m = { critical: "bg-red-100 text-red-800", high: "bg-orange-100 text-orange-800", medium: "bg-amber-100 text-amber-800", low: "bg-emerald-100 text-emerald-800" };
  return <Badge className={`${m[s] || "bg-slate-100 text-slate-700"} border-0 rounded-sm`} data-testid={`sec-finding-sev-${s}`}>{s}</Badge>;
};

export default function SecurityDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get("/security/dashboard");
      setData(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Failed to load security dashboard");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="p-8 text-sm text-slate-600">Loading security signals…</div>;
  if (error) return (
    <div className="p-8" data-testid="security-dashboard-error">
      <div className="border border-red-200 bg-red-50 text-red-900 rounded-md p-6 max-w-xl">
        <div className="font-semibold mb-2">Could not load Security Dashboard</div>
        <div className="text-sm">{error}</div>
        <div className="text-xs mt-3 text-red-700">
          This view is restricted to staff (admin / support / auditor) accounts. Sign in with a staff credential.
        </div>
      </div>
    </div>
  );
  if (!data) return null;

  const evidence = !!data.evidence_mode;

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="security-dashboard">
      <header className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Internal · Security</div>
          <h1 className="font-heading text-3xl text-slate-900 mt-1">Security Dashboard</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Live operational view of authentication, access, backups and control gaps for CoParent Console.
            {evidence && (
              <span className="ml-2 inline-block bg-indigo-100 text-indigo-800 text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-sm" data-testid="security-dashboard-evidence-mode">
                Evidence mode (auditor)
              </span>
            )}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="security-dashboard-refresh">
          <RefreshCcw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </header>

      {/* Top-row metric cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-5" data-testid="security-card-overall-score">
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider">
            <ShieldCheck className="w-4 h-4" /> Overall score
          </div>
          <div className="text-3xl font-semibold text-slate-900 mt-3">{data.overall_score}<span className="text-base text-slate-500 ml-1">/10</span></div>
          <div className="text-xs text-slate-500 mt-1">Weighted across 8 control domains</div>
        </Card>
        <Card className="p-5" data-testid="security-card-failed-logins">
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider">
            <KeyRound className="w-4 h-4" /> Failed logins · 24h
          </div>
          <div className="text-3xl font-semibold text-slate-900 mt-3">{fmt(data.counts.failed_logins_24h)}</div>
          <div className="text-xs text-slate-500 mt-1">Source IPs surfaced below</div>
        </Card>
        <Card className="p-5" data-testid="security-card-active-sessions">
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider">
            <Activity className="w-4 h-4" /> Active sessions
          </div>
          <div className="text-3xl font-semibold text-slate-900 mt-3">{fmt(data.counts.active_sessions)}</div>
          <div className="text-xs text-slate-500 mt-1">Cluster-wide, unrevoked</div>
        </Card>
        <Card className="p-5" data-testid="security-card-users">
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider">
            <Users2 className="w-4 h-4" /> Users
          </div>
          <div className="text-3xl font-semibold text-slate-900 mt-3">{fmt(data.counts.users_active)}</div>
          <div className="text-xs text-slate-500 mt-1">
            {fmt(data.counts.users_archived)} archived · {fmt(data.counts.staff)} staff
          </div>
        </Card>
      </div>

      {/* Open findings */}
      <Card className="p-5 mb-6" data-testid="security-card-findings">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" /> Open security findings
          </h2>
          <span className="text-xs text-slate-500">{data.open_findings?.length || 0} open</span>
        </div>
        <ul className="divide-y divide-slate-100">
          {(data.open_findings || []).map((f) => (
            <li key={f.id} className="py-3 flex items-center gap-3 text-sm" data-testid={`security-finding-${f.id}`}>
              <SeverityPill s={f.severity} />
              <span className="text-slate-500 font-mono text-[11px]">{f.id}</span>
              <span className="text-slate-900 flex-1">{f.title}</span>
              <span className="text-xs text-slate-600 uppercase">{(f.status || "").replace("_", " ")}</span>
            </li>
          ))}
        </ul>
      </Card>

      {/* Score breakdown */}
      <Card className="p-5 mb-6" data-testid="security-card-scores">
        <h2 className="text-sm font-semibold text-slate-800 mb-3">Maturity by domain</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          {Object.entries(data.score_breakdown || {}).map(([k, v]) => (
            <div key={k} className="border border-slate-200 rounded-sm p-3" data-testid={`security-score-${k}`}>
              <div className="text-[11px] uppercase tracking-wide text-slate-500">{k.replace(/_/g, " ")}</div>
              <div className="text-xl font-semibold text-slate-900 mt-1">{v}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Recent admin actions (staff only — not visible in evidence mode) */}
      {!evidence && (
        <Card className="p-5 mb-6" data-testid="security-card-recent-admin">
          <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-3">
            <FileCheck className="w-4 h-4 text-slate-600" /> Recent admin actions
          </h2>
          {(data.recent_admin_actions || []).length === 0 ? (
            <div className="text-xs text-slate-500">No staff actions logged in this window.</div>
          ) : (
            <ul className="divide-y divide-slate-100 text-sm">
              {data.recent_admin_actions.map((a) => (
                <li key={a.id} className="py-2 flex flex-wrap items-center gap-2">
                  <span className="font-mono text-[11px] text-slate-500">{a.created_at?.slice(0, 19)}</span>
                  <span className="font-medium text-slate-900">{a.action}</span>
                  <span className="text-slate-500">· {a.entity}</span>
                  {a.reason && <span className="text-xs text-slate-600 italic">"{a.reason}"</span>}
                  <span className="ml-auto text-[11px] text-slate-500 font-mono">{a.actor_role}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {/* Backup + cert + env health */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-5" data-testid="security-card-backup">
          <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-3">
            <Server className="w-4 h-4" /> Backups
          </h3>
          <dl className="text-xs space-y-1.5 text-slate-700">
            <div className="flex justify-between"><dt>Provider</dt><dd>{data.backup_status?.provider}</dd></div>
            <div className="flex justify-between"><dt>Encryption</dt><dd>{data.backup_status?.encryption_at_rest}</dd></div>
            <div className="flex justify-between"><dt>PITR enabled</dt><dd>{data.backup_status?.pitr_enabled ? "Yes" : "No"}</dd></div>
            <div className="flex justify-between"><dt>Last drill</dt><dd>{data.backup_status?.last_restore_drill || "Not yet drilled"}</dd></div>
          </dl>
        </Card>
        <Card className="p-5" data-testid="security-card-certs">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Certificates</h3>
          {(data.certificates || []).map((c) => (
            <div key={c.hostname} className="text-xs text-slate-700 space-y-1">
              <div className="font-medium text-slate-900">{c.hostname}</div>
              <div>Issuer: {c.issuer || "—"}</div>
              <div>Auto-renew: {c.auto_renews ? "Yes" : "No"}</div>
              <div>Expiry: {c.expires_at || "(provider managed)"}</div>
            </div>
          ))}
        </Card>
        <Card className="p-5" data-testid="security-card-env">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Environment health</h3>
          <ul className="text-xs text-slate-700 space-y-1.5">
            {Object.entries(data.environment_health || {}).map(([k, v]) => (
              <li key={k} className="flex justify-between">
                <span className="capitalize">{k.replace(/_/g, " ")}</span>
                <span className={v === "ok" ? "text-emerald-700" : "text-amber-700"}>{v}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      {/* v77.99.117 — Antivirus control surface. Surfaces engine,
          version, last-30d scan outcomes and quarantine count. The
          orange dot on the header warns when the production
          environment is still on the NoOp engine — the dashboard's
          loudest "do not certify yet" signal. */}
      {data.antivirus && (
        <Card className="p-5 mb-6" data-testid="security-card-antivirus">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
            <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
              <span className={`inline-block w-2 h-2 rounded-full ${data.antivirus.is_production_engine ? "bg-emerald-500" : "bg-amber-500"}`} />
              Antivirus scanning
            </h3>
            <div className="text-xs text-slate-600">
              Engine: <span className="font-mono">{data.antivirus.engine || "unknown"}</span>
              {data.antivirus.engine_version ? ` · v${data.antivirus.engine_version}` : ""}
              {data.antivirus.is_production_engine ? "" : (
                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-900 text-[10px] font-medium" data-testid="security-av-noop-warning">
                  Non-production engine
                </span>
              )}
            </div>
          </div>
          <dl className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-xs text-slate-700">
            <div data-testid="security-av-clean">
              <dt className="text-[10px] uppercase tracking-wider text-slate-500">Clean (30d)</dt>
              <dd className="text-lg font-semibold text-emerald-700">{data.antivirus.scans_30d?.clean ?? 0}</dd>
            </div>
            <div data-testid="security-av-infected">
              <dt className="text-[10px] uppercase tracking-wider text-slate-500">Infected (30d)</dt>
              <dd className="text-lg font-semibold text-rose-700">{data.antivirus.scans_30d?.infected ?? 0}</dd>
            </div>
            <div data-testid="security-av-error">
              <dt className="text-[10px] uppercase tracking-wider text-slate-500">Errors (30d)</dt>
              <dd className="text-lg font-semibold text-amber-700">{data.antivirus.scans_30d?.error ?? 0}</dd>
            </div>
            <div data-testid="security-av-timeout">
              <dt className="text-[10px] uppercase tracking-wider text-slate-500">Timeouts (30d)</dt>
              <dd className="text-lg font-semibold text-amber-700">{data.antivirus.scans_30d?.timeout ?? 0}</dd>
            </div>
            <div data-testid="security-av-quarantine">
              <dt className="text-[10px] uppercase tracking-wider text-slate-500">Quarantined total</dt>
              <dd className="text-lg font-semibold text-slate-900">{data.antivirus.quarantined_total ?? 0}</dd>
            </div>
          </dl>
          <p className="text-[11px] text-slate-500 mt-3">
            Every uploaded file is scanned before release. Failures and detections are quarantined automatically and recorded in the audit log (ISO 27001 A.8.7 / SOC 2 CC6.8).
          </p>
        </Card>
      )}

      <div className="text-[11px] text-slate-500 mt-8">
        Generated at {data.generated_at}. Every view of this page is recorded as an audit-log entry per the Access Control Policy.
      </div>
    </div>
  );
}
